/**
 * PatientSearch.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Campo de busca de paciente com autocomplete instantâneo.
 * Filtra por nome ou telefone com debounce de 150ms.
 * Suporta navegação por teclado (↑↓ Enter Escape).
 *
 * @module PatientSearch
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './PatientSearch.module.css';
import { getInitials } from '../../utils/appointmentUtils';

/** Máximo de resultados exibidos no dropdown — sem limite para mostrar todos */
const MAX_RESULTS = Infinity;

/** Quantos pacientes aparecem ao abrir o campo, antes de digitar */
const INITIAL_RESULTS = 6;

/** Debounce em ms para a busca */
const DEBOUNCE_MS = 150;

/**
 * Normaliza string para comparação (remove acentos, lowercase).
 *
 * @param {string} str
 * @returns {string}
 */
function normalize(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Filtra e ordena pacientes por nome ou telefone.
 *
 * Prioridade de ordenação:
 *  1. Nome começa com o termo buscado
 *  2. Alguma palavra do nome começa com o termo buscado
 *  3. Nome contém o termo em qualquer posição
 *  4. Telefone contém o termo
 *
 * Sem termo digitado, devolve os primeiros pacientes em ordem alfabética.
 *
 * @param {Array} patients
 * @param {string} query
 * @returns {Array}
 */
function filterPatients(patients, query) {
  if (!query || query.trim().length < 1) {
    return patients
      .filter(p => !p.deleted_at)
      .map(p => ({ patient: p, name: normalize(p.name || p.Name || '') }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, INITIAL_RESULTS)
      .map(m => m.patient);
  }
  const q = normalize(query.trim());

  const matched = patients
    .filter(p => !p.deleted_at) // LGPD: paciente excluído (lápide) não é agendável
    .map(p => {
      const name = normalize(p.name || p.Name || '');
      const phone = normalize(p.phone || p.Phone || p.cellphone || p.Cellphone || '');

      const nameStartsWith = name.startsWith(q);
      const wordStartsWith = !nameStartsWith && name.split(/\s+/).some(w => w.startsWith(q));
      const nameContains = name.includes(q);
      const phoneContains = phone.includes(q);

      if (!nameContains && !phoneContains) return null;

      // Prioridade: 0 = melhor, 3 = pior
      let priority;
      if (nameStartsWith) priority = 0;
      else if (wordStartsWith) priority = 1;
      else if (nameContains) priority = 2;
      else priority = 3; // só telefone bate

      return { patient: p, priority };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      // Dentro da mesma prioridade, ordena alfabeticamente
      const nameA = normalize(a.patient.name || a.patient.Name || '');
      const nameB = normalize(b.patient.name || b.patient.Name || '');
      return nameA.localeCompare(nameB);
    });

  return matched.map(m => ({ ...m.patient, _priority: m.priority }));
}

/**
 * Quebra o texto em partes, destacando o trecho que bate com a query.
 * Retorna array de { text, highlight }.
 *
 * @param {string} text
 * @param {string} query
 * @returns {Array<{text: string, highlight: boolean}>}
 */
function highlightMatch(text, query) {
  if (!query) return [{ text, highlight: false }];
  const normalizedText = normalize(text);
  const normalizedQuery = normalize(query.trim());
  const idx = normalizedText.indexOf(normalizedQuery);
  if (idx === -1) return [{ text, highlight: false }];
  return [
    { text: text.slice(0, idx), highlight: false },
    { text: text.slice(idx, idx + query.trim().length), highlight: true },
    { text: text.slice(idx + query.trim().length), highlight: false },
  ].filter(p => p.text.length > 0);
}

/**
 * Formata telefone para exibição.
 *
 * @param {string} phone
 * @returns {string}
 */
function formatPhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

/**
 * Campo de busca de paciente com autocomplete.
 *
 * @param {Object} props
 * @param {Array} props.patients - Lista de pacientes do DataProvider
 * @param {Object|null} props.selectedPatient - Paciente selecionado
 * @param {Function} props.onSelect - (patient) => void
 * @param {Function} [props.onCreateNew] - () => void — criar novo paciente
 * @returns {React.ReactElement}
 */
/** Ícone de busca SVG inline */
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

export default function PatientSearch({
  patients = [],
  selectedPatient,
  onSelect,
  onCreateNew,
  onQueryChange,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // ── Debounce da busca ───────────────────────────────────────────────────────

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const filtered = filterPatients(patients, query);
      setResults(filtered);
      setHighlightedIndex(-1);
      // Apagou o texto com o campo em foco: volta para a lista inicial
      setIsOpen(query.trim().length > 0 || document.activeElement === inputRef.current);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, patients]);

  // ── Fecha ao clicar fora ────────────────────────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    setQuery(val);
    onQueryChange?.(val);
  }, [onQueryChange]);

  const handleSelect = useCallback((patient) => {
    onSelect?.(patient);
    setQuery('');
    setIsOpen(false);
    setResults([]);
  }, [onSelect]);

  const handleClear = useCallback(() => {
    onSelect?.(null);
    setQuery('');
    setIsOpen(false);
    setResults([]);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [onSelect]);

  const handleCreateNew = useCallback(() => {
    const currentQuery = query;
    setIsOpen(false);
    setQuery('');
    onCreateNew?.(currentQuery.trim());
  }, [onCreateNew, query]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    const totalItems = results.length + (onCreateNew ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelect(results[highlightedIndex]);
        } else if (highlightedIndex === results.length && onCreateNew) {
          handleCreateNew();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  }, [isOpen, results, highlightedIndex, handleSelect, handleCreateNew, onCreateNew]);

  // ── Render: paciente selecionado (chip) ─────────────────────────────────────

  if (selectedPatient) {
    const name = selectedPatient.name || selectedPatient.Name || '';
    const phone = selectedPatient.phone || selectedPatient.Phone || selectedPatient.cellphone || '';
    return (
      <div className={styles.selectedChip}>
        <div className={styles.chipAvatar}>
          {getInitials(name)}
        </div>
        <div className={styles.chipInfo}>
          <span className={styles.chipName}>{name}</span>
          {phone && <span className={styles.chipPhone}>{formatPhone(phone)}</span>}
        </div>
        <button
          className={styles.chipClear}
          onClick={handleClear}
          type="button"
          aria-label="Remover paciente"
          title="Remover paciente"
        >
          ×
        </button>
      </div>
    );
  }

  // ── Render: campo de busca ──────────────────────────────────────────────────

  return (
    <div className={styles.searchWrapper}>
      <div className={styles.inputWrapper}>
        <span className={styles.searchIcon} aria-hidden="true"><SearchIcon /></span>
        <input
          ref={inputRef}
          type="text"
          className={styles.searchInput}
          placeholder="Buscar paciente..."
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          onClick={() => setIsOpen(true)}
          autoComplete="off"
          aria-label="Buscar paciente"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />
      </div>

      {/* ── Dropdown ──────────────────────────────────────────────────────── */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          role="listbox"
          aria-label="Resultados da busca"
        >
          {results.length === 0 && !onCreateNew && (
            <div className={styles.noResults}>
              Nenhum paciente encontrado
            </div>
          )}

          {results.map((patient, index) => {
            const name = patient.name || patient.Name || '';
            const phone = patient.phone || patient.Phone || patient.cellphone || '';
            const isHighlighted = index === highlightedIndex;

            return (
              <button
                key={patient.id || patient.Id || index}
                className={`${styles.dropdownItem} ${isHighlighted ? styles.dropdownItemHighlighted : ''}`}
                onClick={() => handleSelect(patient)}
                type="button"
                role="option"
                aria-selected={isHighlighted}
              >
                <div className={styles.itemAvatar}>
                  {getInitials(name)}
                </div>
                <div className={styles.itemInfo}>
                  <span className={styles.itemName}>
                    {highlightMatch(name, query).map((part, i) =>
                      part.highlight
                        ? <mark key={i} className={styles.highlight}>{part.text}</mark>
                        : <span key={i}>{part.text}</span>
                    )}
                  </span>
                  {phone && (
                    <span className={styles.itemPhone}>{formatPhone(phone)}</span>
                  )}
                </div>
              </button>
            );
          })}

          {/* Opção "Novo paciente" */}
          {onCreateNew && (
            <>
              {results.length > 0 && <div className={styles.dropdownDivider} />}
              <button
                className={`${styles.dropdownItem} ${styles.dropdownItemNew} ${highlightedIndex === results.length ? styles.dropdownItemHighlighted : ''}`}
                onClick={handleCreateNew}
                type="button"
                role="option"
              >
                <div className={`${styles.itemAvatar} ${styles.itemAvatarNew}`}>
                  +
                </div>
                <div className={styles.itemInfo}>
                  <span className={styles.itemName}>Novo paciente</span>
                  {query.trim() && (
                    <span className={styles.itemPhone}>Cadastrar "{query.trim()}"</span>
                  )}
                </div>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
